import React from 'react';

interface TableProductInfoProps<T> {
  header: string[];
  data: T[];
  topHeaderGroups: { label: string; colSpan: number; className?: string }[];
  highlightedRows?: Set<number>;
  onRowClick?: (index: number, event: React.MouseEvent) => void;
}

export const TableProductInfo = <T extends Record<string, any>>({
  header,
  data,
  topHeaderGroups,
  highlightedRows = new Set(),
  onRowClick,
}: TableProductInfoProps<T>) => {
  const getHeaderCellClasses = (index: number) => {
    let classes = 'px-4 py-2 bg-slate50 sticky top-[40px] z-20 whitespace-pre-line';
    if (index === 0) {
      classes += ' sticky left-0 z-30 bg-slate50';
    }
    return classes;
  };

  const getDataCellClasses = (colIndex: number, rowIndex: number) => {
    let classes = `px-4 py-2 align-top whitespace-nowrap`;
    if (colIndex === 0) {
      classes += ' sticky left-0 text-sm z-10';
      if (highlightedRows.has(rowIndex)) {
        classes += ' bg-rowHighlight';
      } else {
        classes += ' bg-white';
      }
    }
    return classes;
  };

  const getRowClasses = (index: number) => {
    let classes = '';
    if (highlightedRows.has(index)) {
      classes += ' bg-rowHighlight';
    } else {
      classes += ' bg-white';
    }
    // This makes the row appear clickable (little hand cursor effect when hovering)
    if (onRowClick) {
      classes += ' cursor-pointer hover:bg-gray-50';
    }
    return classes;
  };

  if (data.length) {
    return (
      <div className="overflow-auto max-h-screen">
        <table className="min-w-full table-auto rounded border border-separate text-center">
          <tr>
            {topHeaderGroups.map((g, i) => (
              <th
                key={i}
                colSpan={g.colSpan}
                className={`sticky top-0 z-40 left-0 px-4 py-2 ${g.className || ''}`}
              >
                {g.label}
              </th>
            ))}
          </tr>
          <tr>
            {header.map((column, index) => (
              <th key={index} className={getHeaderCellClasses(index)}>
                {column}
              </th>
            ))}
          </tr>
          <tbody>
            {data.map((item, index) => (
              <tr
                key={index}
                className={getRowClasses(index)}
                onClick={(event) => onRowClick?.(index, event)} //need the event to figure out if command click etc
              >
                {header.map((column, colIndex) => (
                  <td key={colIndex} className={getDataCellClasses(colIndex, index)}>
                    {Array.isArray(item[column]) ? (
                      <ul>
                        {item[column].map((listItem: string, listIndex: number) => (
                          <li key={String(listIndex)} className="whitespace-nowrap text-left">
                            {listItem}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      item[column]
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }
  return null;
};
